import { Form } from "@inertiajs/react"
import { Mail, Pencil, Plus } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOnline } from "@/hooks/use-online"
import { toMessages } from "@/lib/form-errors"
import { candidatePath } from "@/routes"

/**
 * OKS-601 — Candidate contact-email field (UX Spec §2, consolidated).
 *
 * A single, non-redundant surface for the optional contact email,
 * embedded as a row in the profile card's stats `<dl>` (Mail icon,
 * consistent with the Followers / Location / Added rows). It toggles
 * **read ↔ inline-edit in place**, mirroring the OKS-18
 * `candidate-notes-editor` read/edit semantics — there is no separate
 * display + editor (which would double-display the value).
 *
 * **Read mode** (default, and the only mode when `!canEdit`):
 *   - present → `mailto:` link, truncated with the full value in `title`.
 *   - absent  → muted "No email" placeholder. When `canEdit`, the
 *     placeholder is paired with an actionable "Add email" trigger
 *     (PO ask — empty state must read as an affordance, not dead text).
 *
 * **Edit mode** (`canEdit` only): Inertia `<Form>` PATCH to
 * `candidatePath(id)`. **Crew rule — Inertia `<Form>` + plain shadcn
 * `Input`/`Label` with `name="candidate[email]"`.** No shadcn Form, no
 * react-hook-form, no zod. Errors render from the render-prop
 * `errors.email`.
 *
 * **Wire-format gotcha (project memory `playwright_form_canary`):** the
 * `name=` MUST be `candidate[email]`, not `email`. Top-level `email`
 * makes Inertia POST `{email: "..."}` which trips
 * `params.require(:candidate)` → 400 → silent failure. Same trap as
 * notes/tags.
 *
 * Blank is always valid — a whitespace-only save clears the email to
 * NULL server-side (`Candidate` normalizer). Save is disabled while the
 * value is unchanged (no-op-submit prevention) and while offline.
 * `onSuccess` returns the row to read mode; the redirected show props
 * carry the updated value.
 *
 * Locked testids (frozen bilaterally with qa-lead, OKS-601 TRD §4):
 *   - `candidate-detail-email` — row root / read display
 *   - `candidate-detail-email-edit-trigger` — Add/Edit trigger (canEdit)
 *   - `candidate-detail-email-form` — `<form>` element
 *   - `candidate-detail-email-input` — Input
 *   - `candidate-detail-email-submit` — Save button
 *   - `candidate-detail-email-cancel` — Cancel button
 *   - `candidate-detail-email-error` — Error region
 */
interface CandidateEmailFieldProps {
  candidateId: number
  email: string | null
  canEdit: boolean
}

export function CandidateEmailField({
  candidateId,
  email,
  canEdit,
}: CandidateEmailFieldProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const serverValue = email ?? ""
  const [editing, setEditing] = useState(false)

  if (canEdit && editing) {
    return (
      <div className="basis-full" data-testid="candidate-detail-email">
        <EmailEditForm
          candidateId={candidateId}
          serverValue={serverValue}
          online={online}
          onDone={() => setEditing(false)}
        />
      </div>
    )
  }

  return (
    <div
      data-testid="candidate-detail-email"
      className="inline-flex items-center gap-2"
    >
      <Mail
        aria-label={t("candidates.detail.profile.email_aria")}
        className="size-4 shrink-0"
      />
      <dt className="sr-only">{t("candidates.detail.profile.email_aria")}</dt>
      <dd className="inline-flex min-w-0 items-center gap-2">
        {serverValue ? (
          <a
            href={`mailto:${serverValue}`}
            title={serverValue}
            className="hover:text-foreground truncate underline underline-offset-2"
            data-testid="candidate-detail-email-link"
          >
            {serverValue}
          </a>
        ) : (
          <span className="text-muted-foreground italic">
            {t("candidates.detail.email.placeholder_empty")}
          </span>
        )}
        {canEdit && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-1.5 py-0.5"
            onClick={() => setEditing(true)}
            data-testid="candidate-detail-email-edit-trigger"
          >
            {serverValue ? (
              <>
                <Pencil className="size-3.5" aria-hidden="true" />
                <span>{t("candidates.detail.email.edit")}</span>
              </>
            ) : (
              <>
                <Plus className="size-3.5" aria-hidden="true" />
                <span>{t("candidates.detail.email.add")}</span>
              </>
            )}
          </Button>
        )}
      </dd>
    </div>
  )
}

interface EmailEditFormProps {
  candidateId: number
  serverValue: string
  online: boolean
  onDone: () => void
}

/**
 * Inline single-line edit form. Split out so the local dirty-tracking
 * state lives alongside the Inertia `<Form>` render-prop without
 * leaking into the read-mode branch.
 */
function EmailEditForm({
  candidateId,
  serverValue,
  online,
  onDone,
}: EmailEditFormProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState(serverValue)
  const dirty = value !== serverValue

  return (
    <Form
      action={candidatePath(candidateId)}
      method="patch"
      options={{ preserveScroll: true }}
      onSuccess={onDone}
      data-testid="candidate-detail-email-form"
      className="flex flex-col gap-2"
    >
      {({ errors, processing }) => (
        <>
          <Label htmlFor="candidate-email-input" className="sr-only">
            {t("candidates.detail.email.section_label")}
          </Label>
          <div className="flex flex-wrap items-center gap-2">
            <Mail className="size-4 shrink-0" aria-hidden="true" />
            <Input
              id="candidate-email-input"
              name="candidate[email]"
              // OKS-601 — `type="text"` (NOT `type="email"`) + `inputMode`.
              // Native `type="email"` constraint validation BLOCKS the form
              // submit for an invalid address, so the PATCH never fires and
              // the server-authoritative inline error (PRD AC5 "invalid …
              // rejected with an inline error on add or edit") never renders
              // — the editor just sits there (LQE EE4: no PATCH in the trace).
              // Mirrors the OKS-348 country/city fields, which likewise drop
              // HTML5 constraints so Rails validation drives the inline error.
              // `inputMode="email"` keeps the email keyboard on mobile.
              type="text"
              inputMode="email"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t("candidates.detail.email.placeholder")}
              disabled={processing}
              aria-invalid={errors.email ? "true" : undefined}
              aria-describedby="candidate-email-error"
              data-testid="candidate-detail-email-input"
              className="min-w-0 flex-1"
            />
          </div>
          <div
            id="candidate-email-error"
            data-testid="candidate-detail-email-error"
          >
            {/*
             * `toMessages` normalizes the render-prop error (`string |
             * string[]`) to the `string[]` InputError expects — defensive,
             * mirroring the add dialog's `pickErrorMessages`. The actual
             * EE4 fix was `type="text"` above (native email validation was
             * blocking the submit); once the PATCH fires, the render-prop
             * `errors.email` binds normally and this renders it.
             */}
            <InputError messages={toMessages(errors.email)} />
          </div>
          {!online && (
            <p className="text-muted-foreground text-xs" role="status">
              {t("candidates.detail.email.offline_disabled")}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setValue(serverValue)
                onDone()
              }}
              disabled={processing}
              data-testid="candidate-detail-email-cancel"
            >
              {t("candidates.detail.email.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={!dirty || processing || !online}
              data-testid="candidate-detail-email-submit"
            >
              {processing
                ? t("candidates.detail.email.saving")
                : t("candidates.detail.email.save")}
            </Button>
          </div>
        </>
      )}
    </Form>
  )
}
